import { Popover } from 'antd';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import React, { useEffect, useState } from 'react';

import { ReactComponent as TriangleDown } from '@/app/_icons/triangle-down.svg';
import { minDesktop } from '@/constants/vars.constants';
import { useIndexSearch } from '@/hooks/filters/index-search.hook';
import { useWindowSize } from '@/hooks/window-size.hook';

import styles from './filter.module.scss';

export const Filter: React.FC<{
  content: React.ReactNode;
  children: React.ReactNode;
  onCloseFilter?: () => void;
  contentProps?: boolean;
  mobileVersion?: boolean;
  position?: 'bottom' | 'bottomRight' | 'bottomLeft';
  onCloseMobileFilter?: () => void;
}> = ({
  content,
  children,
  onCloseFilter,
  contentProps,
  mobileVersion,
  position,
  onCloseMobileFilter,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const size = useWindowSize();
  const { clearIndexQuery } = useIndexSearch();
  const handleScroll = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    const scrollHandler = throttle(handleScroll, 500);

    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, []);

  const handleVisibleChange = (visible: boolean) => {
    if (onCloseFilter && !visible) onCloseFilter();

    // When interacting with any of the filters, reset the index search as the two
    // results can't be applied together
    clearIndexQuery();

    if (size?.width < minDesktop && visible) {
      document.body.style.overflow = 'hidden';
    } else if (size?.width < minDesktop && !visible) {
      document.body.style.overflow = 'auto';
    }

    if (!visible) {
      if (size?.width < minDesktop) {
        const filterPopoversClose =
          document.querySelectorAll<HTMLElement>('.mobileFilter');

        filterPopoversClose.forEach((item) => {
          item.style.opacity = '0';
        });
        setTimeout(() => {
          setIsVisible(visible);
        }, 300);
      } else {
        setIsVisible(visible);
      }
    } else {
      setIsVisible(visible);
      if (size?.width < minDesktop) {
        setTimeout(() => {
          const filterPopoversOpen =
            document.querySelectorAll<HTMLElement>('.mobileFilter');

          filterPopoversOpen.forEach((item) => {
            item.style.opacity = '1';
          });
        }, 500);
      }
    }
  };

  const contentWithProps = React.cloneElement(
    content as React.ReactElement<any>,
    { handleVisibleChange, mobileVersion, isVisible, onCloseMobileFilter },
  );

  return (
    <Popover
      placement={position ? position : 'bottomLeft'}
      overlayClassName={cn({ [styles.bodyFilter as string]: !position })}
      trigger="click"
      open={isVisible}
      onOpenChange={handleVisibleChange}
      content={contentProps ? contentWithProps : content}
    >
      <div
        className={cn(styles.filterInput, {
          [styles.filterInputActive as string]: isVisible,
        })}
      >
        {children}
        <TriangleDown className={styles.filterTriangle} />
      </div>
    </Popover>
  );
};
